import { MediaType, ExternalAssetSource } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { BasePolyhavenAsset, Model3DAsset } from "./assets";
import { PolyhavenAsset } from "./types";

/**
 * PolyHaven Provider
 *
 * Provides read-only access to Poly Haven's library of free HDRIs, textures, and 3D models.
 * https://polyhaven.com/
 */
export class PolyHavenProvider extends BaseProvider {
  private static readonly API_BASE = 'https://api.polyhaven.com';

  constructor() {
    super({
      id: ExternalAssetSource.Polyhaven,
      name: 'polyhaven',
      displayName: 'Poly Haven',
      description: 'Free HDRIs, textures, and 3D models',
      baseUrl: PolyHavenProvider.API_BASE,
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [ MediaType.HDRI, MediaType.Texture, MediaType.Model3D ]
      }
    });
  }

  /**
   * Map MediaType to PolyHaven type string
   */
  mapTypeToPolyHavenType(mediaType: MediaType): string {
    switch (mediaType) {
      case MediaType.HDRI:
        return 'hdris';
      case MediaType.Texture:
        return 'textures';
      case MediaType.Model3D:
        return 'models';
      default:
        throw new Error(`Unsupported media type for Poly Haven: ${mediaType}`);
    }
  }

  async getCategories(params?: SearchParams): Promise<string[]> {
    try {
      const types = (params?.types || this.getSupportedTypes()).map(type => this.mapTypeToPolyHavenType(type));
      let allCategories: string[] = ['all']; // Include 'all' as a default category
      for (const type of types) {
        const url = `${this.baseUrl}/categories/${type}`;
        const response = await this.fetchJson<{ categories: string[] }>(url);
        const uniqueCategories = Object.keys(response).filter(category => category !== 'all'); // Exclude 'all' from the fetched categories
        allCategories.push(...uniqueCategories);
      }
      return allCategories;
    } catch (error) {
      throw new Error(`Failed to fetch categories from Poly Haven: ${error}`);
    }
  }

  /**
   * Search for assets in Poly Haven
   * Note: Poly Haven API doesn't have a search endpoint, so we'll fetch all assets
   * and filter client-side. For production, consider caching or implementing a better search strategy.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const types = (params.types || this.getSupportedTypes()).map(type => this.mapTypeToPolyHavenType(type));
    const allAssets: IAsset[] = [];

    // If types are hdris, textures, and models, type=all, otherwise type=hdris, textures, or models
    if (types.length === 3) {
      types.length = 0; // Clear the array
      types.push('all'); // Use 'all' to fetch all assets
    }

    // Fetch assets for each requested type
    for (const type of types) {
      const typeAssets = await this.fetchAssetsByType(type, params);
      allAssets.push(...typeAssets);
    }

    // Apply query filter if provided
    let filteredAssets = allAssets;
    if (params.query) {
      const query = params.query.toLowerCase();
      filteredAssets = allAssets.filter(asset =>
        asset.getName().toLowerCase().includes(query)
      );
    }

    // Apply pagination
    const page = params.page || 1;
    const limit = params.limit || 20;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    return filteredAssets.slice(startIndex, endIndex);
  }

  /**
   * Get a specific asset by ID
   */
  async getAssetById(id: string): Promise<IAsset> {
    // Fetch asset info from the API
    // Note: We need to know the asset type to fetch it
    // For now, we'll try each type until we find it
    // const types = this.getSupportedTypes();

    try {
      const url = `${this.baseUrl}/files/${id}`;
      const response = await this.fetchJson<Record<string, PolyhavenAsset>>(url);

      if (response && response[id]) {
        const asset = response[id];
        return this.wrapAsset({
          ...asset,
          id,
        });
      }
      return Promise.reject(new Error(`Asset with ID ${id} not found in Poly Haven`));
    } catch (error) {
      throw new Error(`Asset with ID ${id} not found in Poly Haven`);
    }
  }

  /**
   * Fetch assets by type from Poly Haven API
   */
  private async fetchAssetsByType(type: string, params: SearchParams): Promise<IAsset[]> {
    try {
      const { category } = params;

      const url = `${this.baseUrl}/assets?t=${type}${category && category !== 'all' ? `&categories=${encodeURIComponent(category)}` : ''}`;
      const response = await this.fetchJson<Record<string, PolyhavenAsset>>(url);

      // Convert the response object to an array of assets
      return Object.entries(response).map(([id, assetData]) => {
        return this.wrapAsset({
          ...assetData,
          id,
        });
      });
    } catch (error) {
      console.error(`Failed to fetch ${type} assets from Poly Haven:`, error);
      return [];
    }
  }

  /**
   * Wrap a PolyHaven asset in the appropriate asset class
   */
  private wrapAsset(assetData: PolyhavenAsset): IAsset {
    // Type 2 is for 3D models
    if (assetData.type === 2) {
      return new Model3DAsset(assetData, this.baseUrl);
    }

    // Default to base asset for HDRIs and textures
    return new BasePolyhavenAsset(assetData, this.baseUrl);
  }
}
